import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    Output,
    ViewChild
} from '@angular/core';
import {ControlValueAccessor, NG_VALUE_ACCESSOR} from '@angular/forms';
import {IJigsawFormControl, JigsawUploadBase, JigsawUploadDirective, JigsawUploadResult, UploadFileInfo} from "@rdkmaster/jigsaw";

@Component({
    selector: 'formly-jigsaw-upload',
    template: `
        <div class="formly-jigsaw-upload-button">
            <jigsaw-button
                [width]="width"
                [disabled]="disabled"
                [colorType]="colorType"
                [preSize]="preSize"
                [icon]="icon"
                [theme]="theme"
                j-upload
                [uploadTargetUrl]="targetUrl"
                [uploadFileType]="fileType"
                [uploadMultiple]="multiple"
                [uploadBatchMode]="batchMode"
                [uploadContentField]="contentField"
                [uploadFileNameField]="fileNameField"
                [uploadFileVerify]="fileVerify"
                [uploadAdditionalFields]="additionalFields"
                [uploadMinSize]="minSize"
                [uploadMaxSize]="maxSize"
                [uploadImmediately]="uploadImmediately"
                (uploadProgress)="progress.emit($event)"
                (uploadDataSendProgress)="dataSendProgress.emit($event)"
                (uploadComplete)="_$complete($event)"
                (uploadStart)="start.emit($event)"
                (uploadChange)="_$uploadChange($event)"
            >{{content}}</jigsaw-button>
            <a *ngIf="clearable && showUploadResult && files.length > 0" class="formly-jigsaw-upload-button-clear"
               [title]="'formUpload.clear' | translate" (click)="clear()">
                <i class="iconfont iconfont-e865"></i>
            </a>
        </div>
        <jigsaw-upload-result *ngIf="showUploadResult && files.length > 0" width="100%"
                              [uploader]="uploader"
                              [theme]="theme"
                              (change)="_$resultChange($event)"
                              (progress)="resultProgress.emit($event)"
                              (remove)="resultRemove.emit($event)">
        </jigsaw-upload-result>
    `,
    styles: [`
        .formly-jigsaw-upload-button {
            width: calc(100% - 22px);
            display: flex;
            align-items: center;
        }

        .formly-jigsaw-upload-button-clear {
            font-size: 16px;
            margin-left: 6px;
        }
    `],
    host: {
        '[style.display]': "'block'",
        '[style.flex]': '1',
        '[style.width]': 'width',
        '[style.height]': 'height',
    },
    providers: [
        {provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => FormlyUploadComponent), multi: true},
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class FormlyUploadComponent extends JigsawUploadBase implements IJigsawFormControl, ControlValueAccessor {
    @ViewChild(JigsawUploadDirective)
    public uploader: JigsawUploadDirective;
    @ViewChild(JigsawUploadResult)
    public uploadResult: JigsawUploadResult;

    /**
     * å½åå°è£çç»ä»¶å±æ§
     */
    @Input()
    public content: string = 'ä¸ä¼ ';
    @Input()
    public showUploadResult: boolean = true;
    @Input()
    public clearable: boolean = true;
    @Input()
    public valid: boolean = true;
    @Input()
    public theme: 'light' | 'dark' | string;

    /**
     * æé®æ¬èº«çå ä¸ªå±æ§
     */
    @Input()
    public disabled: boolean = false;
    @Input()
    public colorType: 'default' | 'primary' | 'warning' | 'error' | 'danger' = 'default';
    @Input()
    public preSize: 'default' | 'small' | 'medium' | 'large' = 'default';
    @Input()
    public icon: string;

    /**
     * ä¸ä¼ ç»æçä¸ä¸ªäºä»¶ï¼è¿éå ä¸åç¼ resultï¼
     * ä¸æ¯ä¸ºäºè· upload æä»¤çäºä»¶ä½åºå
     * äºæ¯å ä¸ºï¼change è¿ä¸ªåç§°ä¹æ¯ TemplateOption éé¢åç½®çå±æ§åç§°ï¼è¿éæ²¡æ³ç´æ¥ä½¿ç¨
     */
    @Output()
    public resultChange: EventEmitter<UploadFileInfo[]> = new EventEmitter<UploadFileInfo[]>();
    @Output()
    public resultProgress = new EventEmitter<UploadFileInfo>();
    @Output()
    public resultRemove = new EventEmitter<UploadFileInfo>();

    constructor(private _changeDetectorRef: ChangeDetectorRef) {
        super();
    }

    /**
     * è¿åå½åä¸ä¼ çæä»¶ä¿¡æ¯
     */
    public get files(): UploadFileInfo[] {
        return this.uploader ? this.uploader.files : [];
    }

    /**
     * éç½®ä¸ºæå¨ä¸ä¼ æ¶ï¼è°ç¨æ­¤æ¹æ³è¿è¡æä»¶ä¸ä¼ 
     */
    public upload(): void {
        this.uploader?.upload();
    }

    /**
     * æ¸ç©ºä¸ä¼ ç»æ
     */
    public clear(): void {
        this.uploadResult?.clear();
        this._propagateChange([]);
    }

    public _$uploadChange($event: UploadFileInfo[]): void {
        this.change.emit($event);
        this._propagateChange($event);
    }

    public _$complete($event: UploadFileInfo[]): void {
        this.complete.emit($event);
        this._propagateChange($event);
    }

    public _$resultChange($event: UploadFileInfo[]): void {
        this.resultChange.emit($event);
        this._propagateChange($event);
    }

    protected _propagateChange: any = () => {
    };
    protected _onTouched: any = () => {
    };

    registerOnChange(fn: any): void {
        this._propagateChange = fn;
    }

    registerOnTouched(fn: any): void {
        this._onTouched = fn;
    }

    writeValue(value: any): void {
        this._propagateChange(value);
        this._changeDetectorRef.markForCheck();
    }

    @HostListener('click')
    onClickTrigger(): void {
        if (this.disabled) {
            return;
        }
        this._onTouched();
    }

    public setDisabledState(disabled: boolean): void {
        this.disabled = disabled;
    }
}
